import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import * as THREE from 'three';
import type { BVH } from 'three/examples/jsm/loaders/BVHLoader.js';
import { subscribe } from 'valtio';
import {
  Player,
  usePlayerActions,
  usePlayerSettings,
  usePlayerTimeline,
} from '@cartwhl/3d/player';
import { loadBVHAnimation } from '@cartwhl/3d/player/hooks/useBVHAnimation';
import { PlayerStoreContext } from '@cartwhl/3d/player/store/playerStore';
import useFrameMixer from '@cartwhl/3d/r3f/hooks/useFrameMixer';
import type { MotionConfigSource } from '@cartwhl/common/api/getMotion';
import Slider from '@cartwhl/ui/atoms/Slider';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';
import ExpandIcon from '@cartwhl/ui/icons/ExpandIcon';
import PauseIcon from '@cartwhl/ui/icons/PauseIcon';
import PlayIcon from '@cartwhl/ui/icons/PlayIcon';
import Button from '@cartwhl/ui/input/Button';
import GoButton from '@cartwhl/ui/input/GoButton';

import {
  Context as CartographerStoreContext,
  type CartographerPoseSnapshot,
} from '../../../cartographerStore';
import {
  createThreeSkeletonPoseBinding,
  sampleThreeSkeletonPoseLocals,
} from '../../../lib/skeleton/sampleThreeSkeletonPoseLocals';
import {
  pushCartographerDebugEvent,
  stringifyDebugReason,
} from '../../../lib/cartographerDebug';
import { RetargetRefreshIcon } from '../../../presentations/icons';
import {
  MOTION_SCENE_PREVIEW_LAYERING,
  MOTION_SCENE_PREVIEW_TIMELINE,
} from '../constants';
import { DEFAULT_MOTION_EXPORT_SETTINGS } from '../motionExport';
import {
  getMotionExportSettingsKey,
  type CartographerMotionRetargetStatus,
} from '../motionRetargetStatus';
import {
  resolveMotionPreviewDownloadType,
  type CartographerMotionPreviewAssetKind,
  type CartographerMotionPreviewGltfAsset,
} from '../motionPreviewAsset';
import useCartographerMotionRetargetPreview from '../useCartographerMotionRetargetPreview';
import useMotionPreviewGltfAsset from '../useMotionPreviewGltfAsset';

const STAGE_PREVIEW_PLAYER_ID = 'cartographer-scene-motion-preview';
const STAGE_PREVIEW_ACTOR_ID = 'cartographer-scene-motion-preview-actor';
const STAGE_PREVIEW_FRAME_RATE = 30;
const STAGE_PREVIEW_FRAME_STEP = 1;
const STAGE_PREVIEW_TIMELINE_MIN_WIDTH_PX = 360;
const STAGE_PREVIEW_TIMELINE_RESIZE_STEP_PX = 80;
const STAGE_PREVIEW_TIMELINE_RESIZE_CURSOR = 'ew-resize';

function previewSliderDisplayFrame(zeroBased: number) {
  return zeroBased + 1;
}

function previewSliderToZeroBased(display: number) {
  return display - 1;
}

function getStageTimelineDefaultWidth() {
  if (typeof window === 'undefined') {
    return MOTION_SCENE_PREVIEW_TIMELINE.maxWidthPx;
  }
  return Math.max(
    0,
    Math.min(
      MOTION_SCENE_PREVIEW_TIMELINE.maxWidthPx,
      window.innerWidth -
        MOTION_SCENE_PREVIEW_TIMELINE.defaultHorizontalViewportGutterPx,
    ),
  );
}

function getStageTimelineMaxWidth() {
  if (typeof window === 'undefined') {
    return MOTION_SCENE_PREVIEW_TIMELINE.maxWidthPx;
  }
  return Math.max(
    0,
    Math.min(
      MOTION_SCENE_PREVIEW_TIMELINE.maxWidthPx,
      window.innerWidth -
        MOTION_SCENE_PREVIEW_TIMELINE.horizontalViewportGutterPx,
    ),
  );
}

function clampStageTimelineWidth(width: number) {
  const maxWidth = getStageTimelineMaxWidth();
  const minWidth = Math.min(STAGE_PREVIEW_TIMELINE_MIN_WIDTH_PX, maxWidth);
  return Math.min(maxWidth, Math.max(minWidth, width));
}

function sourceBoneHasBoneParent(sourceBone: THREE.Bone): boolean {
  let current: THREE.Object3D | null = sourceBone.parent;
  while (current) {
    if (current instanceof THREE.Bone) return true;
    current = current.parent;
  }
  return false;
}

function collectAnimatedSourceBoneNames(
  clip: THREE.AnimationClip,
  sourceBones: THREE.Bone[],
): Set<string> {
  const sourceBoneNames = new Set(sourceBones.map((bone) => bone.name));
  const animatedBoneNames = new Set<string>();

  for (const track of clip.tracks) {
    try {
      const parsed = THREE.PropertyBinding.parseTrackName(track.name);
      if (parsed.nodeName && sourceBoneNames.has(parsed.nodeName)) {
        animatedBoneNames.add(parsed.nodeName);
      }
    } catch {
      // Ignore tracks that do not target a specific scene node.
    }
  }

  return animatedBoneNames;
}

interface StageTimelineResizeDragState {
  startClientX: number;
  startWidthPx: number;
  cleanup: () => void;
}

interface CartographerSceneMotionPreviewProps {
  apiKey: string;
  characterID: string;
  motionID: string | null;
  motionLabel?: string | null;
  /** Zero-based body index for multi-person comic jobs. */
  bodyIndex?: number;
  fileVersion: number;
  mappingRevisionKey: string | number | null;
  assetKind: CartographerMotionPreviewAssetKind;
  configSource?: MotionConfigSource;
  prepareForUserMotionRequest?: (options: {
    configSource: MotionConfigSource;
    mode: 'auto' | 'manual';
  }) => Promise<boolean> | boolean;
  onSwitchToUnsavedRetarget?: () => Promise<boolean> | boolean;
  onRetargetStatusChange?: (status: CartographerMotionRetargetStatus) => void;
  onPlaybackActiveChange?: (active: boolean) => void;
  onOpenFullPreview: () => void;
  onClose: () => void;
}

interface SceneMotionFooterProps {
  configSource: MotionConfigSource;
  loading: boolean;
  mainError: string | null;
  motionLabel: string;
  previewOutOfDate: boolean;
  motionUrl: string | null;
  onOpenFullPreview: () => void;
  onClose: () => void;
  onRefreshPreview: () => Promise<void>;
}

function SceneMotionFooter({
  configSource,
  loading,
  mainError,
  motionLabel,
  previewOutOfDate,
  motionUrl,
  onOpenFullPreview,
  onClose,
  onRefreshPreview,
}: SceneMotionFooterProps) {
  const { currentFrame, duration, isPlaying } = usePlayerTimeline(
    STAGE_PREVIEW_ACTOR_ID,
  );
  const actions = usePlayerActions(STAGE_PREVIEW_ACTOR_ID);
  const maxFrame = Math.max(duration.frames - 1, 0);
  const clampedFrame = Math.min(Math.max(currentFrame, 0), maxFrame);
  const timelineDisabled = duration.frames <= 0 || !motionUrl;
  const hasRetargetingError = Boolean(mainError && !motionUrl);
  const isInitialRetargeting = !motionUrl && !hasRetargetingError;
  const isTimelineRetargeting = loading && !hasRetargetingError;
  const visibleFrame = isInitialRetargeting ? 0 : clampedFrame;
  const visibleMaxFrame = isInitialRetargeting ? 1 : maxFrame;
  const t = MOTION_SCENE_PREVIEW_TIMELINE;
  const layering = MOTION_SCENE_PREVIEW_LAYERING;
  const [timelineWidthPx, setTimelineWidthPx] = useState(() =>
    getStageTimelineDefaultWidth(),
  );
  const resizeDragStateRef = useRef<StageTimelineResizeDragState | null>(null);
  const timelineWidthCss = `${timelineWidthPx}px`;
  const actionButtons = (
    <div className="flex shrink-0 items-center gap-1">
      {previewOutOfDate && motionUrl ? (
        <Tooltip
          text={
            loading
              ? 'Retargeting using current mapping…'
              : configSource === 'temp'
                ? 'Unsaved mapping changed. Retarget and refresh preview.'
                : 'Mapping changed. Retarget using current in-editor mapping.'
          }
          side="top"
          inModal
        >
          <Button
            variant="tertiary"
            size="sm"
            className="motion-stage-refresh-nudge h-8 w-8"
            disabled={loading}
            icon={<RetargetRefreshIcon className="h-5 w-5" />}
            onClick={() => {
              void onRefreshPreview();
            }}
            aria-label="Retarget and refresh stage preview"
          />
        </Tooltip>
      ) : null}
      <Tooltip text="Open full motion preview" side="top" inModal>
        <Button
          variant="tertiary"
          size="sm"
          className="h-8 w-8"
          icon={<ExpandIcon className="h-4 w-4" />}
          onClick={onOpenFullPreview}
          aria-label="Open full motion preview"
        />
      </Tooltip>
      <Tooltip text="Close stage preview" side="top" inModal>
        <Button
          variant="tertiary"
          size="sm"
          className="h-8 w-8"
          icon={<CrossIcon className="h-4 w-4" />}
          onClick={onClose}
          aria-label="Close stage preview"
        />
      </Tooltip>
    </div>
  );
  const labelBlock = (
    <div className="min-w-0 shrink-0 pr-2">
      <Text
        as="div"
        variant="studio/body/medium/sm"
        className={`truncate text-primary ${previewOutOfDate ? 'italic' : ''}`}
        style={{ maxWidth: t.labelMaxWidthPx }}
        title={motionLabel}
      >
        {motionLabel}
      </Text>
    </div>
  );
  const pauseIfPlayingBeforeScrub = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (timelineDisabled || !isPlaying) return;
      // Primary button / touch / pen - ignore modified clicks reserved for other UI.
      if (event.button !== 0) return;
      actions.pause();
    },
    [actions, isPlaying, timelineDisabled],
  );
  const handleTimelineResizePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (event.button !== 0) return;

      event.preventDefault();
      event.stopPropagation();
      resizeDragStateRef.current?.cleanup();

      const previousBodyCursor = document.body.style.cursor;
      const previousBodyUserSelect = document.body.style.userSelect;
      document.body.style.cursor = STAGE_PREVIEW_TIMELINE_RESIZE_CURSOR;
      document.body.style.userSelect = 'none';

      const handlePointerMove = (moveEvent: PointerEvent) => {
        const dragState = resizeDragStateRef.current;
        if (!dragState) return;
        if ((moveEvent.buttons & 1) === 0) {
          dragState.cleanup();
          return;
        }
        const deltaPixels = moveEvent.clientX - dragState.startClientX;
        setTimelineWidthPx(
          clampStageTimelineWidth(dragState.startWidthPx + deltaPixels * 2),
        );
      };

      const handlePointerUp = () => {
        resizeDragStateRef.current?.cleanup();
      };

      const handleWindowBlur = () => {
        resizeDragStateRef.current?.cleanup();
      };

      const cleanup = () => {
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
        window.removeEventListener('blur', handleWindowBlur);
        document.body.style.cursor = previousBodyCursor;
        document.body.style.userSelect = previousBodyUserSelect;
        if (resizeDragStateRef.current?.cleanup === cleanup) {
          resizeDragStateRef.current = null;
        }
      };

      resizeDragStateRef.current = {
        startClientX: event.clientX,
        startWidthPx: clampStageTimelineWidth(timelineWidthPx),
        cleanup,
      };

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
      window.addEventListener('blur', handleWindowBlur);
    },
    [timelineWidthPx],
  );
  const handleTimelineResizeKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setTimelineWidthPx((width) =>
          clampStageTimelineWidth(
            width - STAGE_PREVIEW_TIMELINE_RESIZE_STEP_PX,
          ),
        );
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        setTimelineWidthPx((width) =>
          clampStageTimelineWidth(
            width + STAGE_PREVIEW_TIMELINE_RESIZE_STEP_PX,
          ),
        );
      } else if (event.key === 'Home') {
        event.preventDefault();
        setTimelineWidthPx(clampStageTimelineWidth(0));
      } else if (event.key === 'End') {
        event.preventDefault();
        setTimelineWidthPx(clampStageTimelineWidth(t.maxWidthPx));
      }
    },
    [t.maxWidthPx],
  );

  useEffect(() => {
    const handleResize = () => {
      setTimelineWidthPx((width) => clampStageTimelineWidth(width));
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      resizeDragStateRef.current?.cleanup();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className="pointer-events-auto relative w-full">
      {layering.enableBottomScrim ? (
        <div className={layering.bottomScrimClassName} aria-hidden />
      ) : null}
      <div className="relative z-10 w-full">
        {layering.enableDockPlate ? (
          <div
            className={layering.dockPlateClassName}
            style={{
              width: timelineWidthCss,
              borderRadius: t.borderRadius + 4,
            }}
            aria-hidden
          />
        ) : null}
        <div
          className={`relative ${layering.cardBaseClassName} ${
            layering.enableElevatedCardSurface
              ? layering.cardElevatedSurfaceClassName
              : layering.cardDefaultSurfaceClassName
          }`}
          style={{
            width: timelineWidthCss,
            paddingLeft: t.paddingX,
            paddingRight: t.paddingX,
            paddingTop: t.paddingY,
            paddingBottom: t.paddingY,
            gap: t.gap,
            borderRadius: t.borderRadius,
          }}
        >
          <div className="flex min-w-0 items-start justify-between gap-2 lg:hidden">
            <div className="min-w-0 flex-1">{labelBlock}</div>
            {actionButtons}
          </div>
          <div className="hidden lg:block">{labelBlock}</div>

          <div className="min-w-0 flex-1">
            {hasRetargetingError ? (
              <div
                className="flex items-center"
                style={{ minHeight: t.rowHeightPx }}
              >
                <Text variant="studio/body/medium/sm" className="text-red-60">
                  {mainError}
                </Text>
              </div>
            ) : (
              <div className="flex min-w-0 flex-nowrap items-center gap-2">
                <GoButton
                  className="h-8 w-8 shrink-0 px-0"
                  onClick={() => actions.playOrPause()}
                  disabled={timelineDisabled}
                >
                  {isPlaying ? (
                    <PauseIcon className="pointer-events-none h-4 w-4" />
                  ) : (
                    <PlayIcon className="pointer-events-none h-4 w-4" />
                  )}
                </GoButton>
                <div
                  className="flex min-w-0 flex-1 basis-0 items-center px-2"
                  style={{ minHeight: t.rowHeightPx }}
                  onPointerDownCapture={pauseIfPlayingBeforeScrub}
                >
                  <Slider
                    className={t.sliderRootClassName}
                    currentValue={previewSliderDisplayFrame(visibleFrame)}
                    min={previewSliderDisplayFrame(0)}
                    max={previewSliderDisplayFrame(visibleMaxFrame)}
                    size="sm"
                    step={1}
                    onChange={(value) => {
                      actions.setFrame(previewSliderToZeroBased(value));
                    }}
                    trackColor="player"
                    rangeColor="player"
                    disabled={timelineDisabled}
                    showThumbWhenDisabled={
                      isInitialRetargeting || isTimelineRetargeting
                    }
                    thumbClassName={
                      isTimelineRetargeting
                        ? 'motion-stage-timeline-thumb-sweep'
                        : ''
                    }
                  />
                </div>
                <Text
                  as="div"
                  variant="studio/body/medium/sm"
                  className="text-secondary ml-auto shrink-0 text-end tabular-nums"
                  style={{
                    minWidth: `${Math.max(duration.frames.toString().length, 3) * t.frameCounterChWidthPx}px`,
                  }}
                >
                  {isInitialRetargeting || isTimelineRetargeting
                    ? 'Retargeting…'
                    : `${clampedFrame + 1}/${duration.frames}`}
                </Text>
              </div>
            )}
          </div>

          <div className="hidden lg:flex">{actionButtons}</div>
          <button
            type="button"
            aria-label="Resize stage preview timeline"
            className="focus-visible group absolute top-1/2 right-0 z-10 flex h-10 w-4 -translate-y-1/2 translate-x-1/2 cursor-ew-resize items-center justify-center rounded-full"
            onPointerDown={handleTimelineResizePointerDown}
            onKeyDown={handleTimelineResizeKeyDown}
          >
            <span className="bg-gray-45/75 group-hover:bg-gray-60 dark:bg-gray-30/75 dark:group-hover:bg-gray-15 h-7 w-1 rounded-full transition-colors" />
          </button>
        </div>
      </div>
    </div>
  );
}

function HeadlessResolvedBvhScenePlayer({
  bvh,
  fileVersion,
  onPlaybackActiveChange,
}: {
  bvh: BVH;
  fileVersion: number;
  onPlaybackActiveChange?: (active: boolean) => void;
}) {
  const cartographerStore = useContext(CartographerStoreContext);
  const playerStore = useContext(PlayerStoreContext);
  const {
    settings: {
      playback: {
        autoplay,
        disabled,
        frameRate,
        frameStepSize,
        loop,
        playbackSpeed,
      },
    },
  } = usePlayerSettings();
  const poseLocalsRef = useRef<THREE.Matrix4[]>([]);
  const suppressPauseRef = useRef(false);
  const firstPoseApplyLoggedRef = useRef(false);
  const firstFrameUpdateLoggedRef = useRef(false);
  const initializedMixerIdRef = useRef<string | null>(null);
  const mixerDurationFramesRef = useRef(0);
  const playbackActiveRef = useRef(false);
  const [playbackActive, setPlaybackActive] = useState(false);

  if (!cartographerStore) {
    throw new Error(
      'CartographerSceneMotionPreview must be rendered inside CartographerStoreProvider.',
    );
  }

  if (!playerStore) {
    throw new Error(
      'CartographerSceneMotionPreview must be rendered inside PlayerStoreProvider.',
    );
  }

  const animationRoot = useMemo(() => {
    const root = new THREE.Group();
    const rootBones = bvh.skeleton.bones.filter((bone) => bone.parent === null);
    for (const bone of rootBones) {
      root.add(bone);
    }
    return root;
  }, [bvh]);

  const poseBinding = useMemo(() => {
    const skeleton = cartographerStore.getSkeleton();
    if (!skeleton) return null;
    return createThreeSkeletonPoseBinding(bvh.skeleton.bones, skeleton);
  }, [bvh, cartographerStore, fileVersion]);

  useEffect(() => {
    const skeleton = cartographerStore.getSkeleton();
    pushCartographerDebugEvent('motionStage.player.mount', {
      fileVersion,
      sourceBoneCount: bvh.skeleton.bones.length,
      targetBoneCount: skeleton?.bones.length ?? 0,
      poseBindingCount: poseBinding?.bindings.length ?? 0,
      clipDurationSeconds: bvh.clip.duration,
      clipTrackCount: bvh.clip.tracks.length,
      clipName: bvh.clip.name,
    });
    return () => {
      pushCartographerDebugEvent('motionStage.player.unmount', {
        fileVersion,
        sourceBoneCount: bvh.skeleton.bones.length,
      });
    };
  }, [bvh, cartographerStore, fileVersion, poseBinding]);

  useEffect(() => {
    const skeleton = cartographerStore.getSkeleton();
    pushCartographerDebugEvent('motionStage.binding.state', {
      hasTargetSkeleton: Boolean(skeleton),
      targetBoneCount: skeleton?.bones.length ?? 0,
      sourceBoneCount: bvh.skeleton.bones.length,
      poseBindingCount: poseBinding?.bindings.length ?? 0,
    });
  }, [bvh.skeleton.bones.length, cartographerStore, poseBinding]);

  const applyCurrentPose = useCallback(
    (source: 'initialFrame' | 'frameUpdate') => {
      const skeleton = cartographerStore.getSkeleton();
      if (!skeleton || !poseBinding || poseBinding.bindings.length === 0) return;

      try {
        suppressPauseRef.current = true;
        sampleThreeSkeletonPoseLocals(poseBinding, skeleton, poseLocalsRef.current);
        cartographerStore.applyPreviewPoseLocals(poseLocalsRef.current, {
          notifyPoseVersion: !playbackActiveRef.current,
        });

        if (!firstPoseApplyLoggedRef.current) {
          firstPoseApplyLoggedRef.current = true;
          pushCartographerDebugEvent('motionStage.pose.apply.first', {
            source,
            targetBoneCount: skeleton.bones.length,
            poseBindingCount: poseBinding.bindings.length,
          });
        }
      } catch (error) {
        pushCartographerDebugEvent('motionStage.pose.apply.error', {
          source,
          error: stringifyDebugReason(error),
          errorStack: error instanceof Error ? error.stack ?? null : null,
          targetBoneCount: skeleton.bones.length,
          poseBindingCount: poseBinding.bindings.length,
        });
        throw error;
      }
    },
    [cartographerStore, poseBinding],
  );
  const handleFrameUpdate = useCallback(
    (frame: number) => {
      playerStore.setCurrentFrame(frame);
      if (!firstFrameUpdateLoggedRef.current) {
        firstFrameUpdateLoggedRef.current = true;
        pushCartographerDebugEvent('motionStage.frame.first', {
          frame,
          durationFrames: mixerDurationFramesRef.current,
        });
      }
      applyCurrentPose('frameUpdate');
    },
    [applyCurrentPose, playerStore],
  );
  const handlePlayChange = useCallback(
    (isPlaying: boolean) => {
      playbackActiveRef.current = isPlaying;
      playerStore.setIsPlaying(isPlaying);
      onPlaybackActiveChange?.(isPlaying);
      setPlaybackActive((current) => (current === isPlaying ? current : isPlaying));
      pushCartographerDebugEvent('motionStage.playback.state', {
        isPlaying,
      });
    },
    [onPlaybackActiveChange, playerStore],
  );
  const handleReady = useCallback(
    (duration: { seconds: number; frames: number }) => {
      mixerDurationFramesRef.current = duration.frames;
      playerStore.setDuration(duration);
      pushCartographerDebugEvent('motionStage.player.ready', {
        durationFrames: duration.frames,
        durationSeconds: duration.seconds,
      });
    },
    [playerStore],
  );

  const mixer = useFrameMixer(bvh.clip, animationRoot, {
    autoplay,
    disabled: disabled || !poseBinding || poseBinding.bindings.length === 0,
    frameRate,
    frameStepSize,
    loop,
    onFrameUpdate: handleFrameUpdate,
    onPlayChange: handlePlayChange,
    onReady: handleReady,
    playbackSpeed,
  });
  const playbackLoopEnabled =
    playbackActive && !disabled && Boolean(poseBinding?.bindings.length);

  useEffect(() => {
    playerStore.setActorMixer(STAGE_PREVIEW_ACTOR_ID, mixer);
    pushCartographerDebugEvent('motionStage.actor.mixer.set', {
      actorId: STAGE_PREVIEW_ACTOR_ID,
    });
    return () => {
      playerStore.setActorMixer(STAGE_PREVIEW_ACTOR_ID, undefined);
      pushCartographerDebugEvent('motionStage.actor.mixer.clear', {
        actorId: STAGE_PREVIEW_ACTOR_ID,
      });
    };
  }, [mixer, playerStore]);

  useEffect(
    () => () => {
      playbackActiveRef.current = false;
      onPlaybackActiveChange?.(false);
    },
    [onPlaybackActiveChange],
  );

  useEffect(() => {
    mixerDurationFramesRef.current = mixer.duration.frames;
    playerStore.setDuration(mixer.duration);
  }, [mixer.duration, playerStore]);

  useEffect(() => {
    if (mixer.duration.frames <= 0) return;
    if (initializedMixerIdRef.current === mixer.id) return;

    initializedMixerIdRef.current = mixer.id;
    pushCartographerDebugEvent('motionStage.player.initialFrame', {
      durationFrames: mixer.duration.frames,
    });
    mixer.setFrame(0);
    applyCurrentPose('initialFrame');
  }, [applyCurrentPose, mixer, mixer.duration.frames]);

  useEffect(() => {
    if (!playbackLoopEnabled) return;

    let animationFrameId = 0;
    let previousTime = performance.now();

    const tick = (time: number) => {
      const delta = Math.max(0, (time - previousTime) / 1000);
      previousTime = time;
      mixer.update(delta);

      if (mixer.getPlayStatus()) {
        animationFrameId = window.requestAnimationFrame(tick);
        return;
      }

      setPlaybackActive(false);
    };

    animationFrameId = window.requestAnimationFrame((time) => {
      previousTime = time;
      animationFrameId = window.requestAnimationFrame(tick);
    });

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [mixer, playbackLoopEnabled]);

  useEffect(() => {
    return subscribe(cartographerStore.state, (operations) => {
      const poseVersionChanged = operations.some(
        ([, path]) => path[0] === 'poseVersion',
      );
      if (!poseVersionChanged) return;

      if (suppressPauseRef.current) {
        suppressPauseRef.current = false;
        return;
      }

      pushCartographerDebugEvent('motionStage.playback.paused.externalPoseChange');
      playerStore.pause(STAGE_PREVIEW_ACTOR_ID);
    });
  }, [cartographerStore, playerStore]);

  return null;
}

function HeadlessBvhScenePlayer({
  bvhUrl,
  fileVersion,
  onPlaybackActiveChange,
}: {
  bvhUrl: string;
  fileVersion: number;
  onPlaybackActiveChange?: (active: boolean) => void;
}) {
  const playerStore = useContext(PlayerStoreContext);
  const [resolvedBvh, setResolvedBvh] = useState<BVH | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);

  if (!playerStore) {
    throw new Error(
      'CartographerSceneMotionPreview must be rendered inside PlayerStoreProvider.',
    );
  }

  useEffect(() => {
    playerStore.registerActor(STAGE_PREVIEW_ACTOR_ID);
    playerStore.setActiveActor(STAGE_PREVIEW_ACTOR_ID);
    pushCartographerDebugEvent('motionStage.actor.register', {
      actorId: STAGE_PREVIEW_ACTOR_ID,
      fileVersion,
    });
    return () => {
      playerStore.unregisterActor(STAGE_PREVIEW_ACTOR_ID);
      pushCartographerDebugEvent('motionStage.actor.unregister', {
        actorId: STAGE_PREVIEW_ACTOR_ID,
      });
    };
  }, [fileVersion, playerStore]);

  useEffect(() => {
    let isMounted = true;
    setResolvedBvh(null);
    setLoadError(null);
    setIsLoading(true);
    playerStore.setActorAnimationStatus(STAGE_PREVIEW_ACTOR_ID, 'pending');
    playerStore.setActorAssetError(STAGE_PREVIEW_ACTOR_ID, null);

    void loadBVHAnimation(bvhUrl)
      .then((bvh) => {
        if (!isMounted) return;
        setResolvedBvh(bvh ?? null);
        setIsLoading(false);
        setLoadError(null);
        playerStore.setActorAnimationStatus(
          STAGE_PREVIEW_ACTOR_ID,
          bvh ? 'ready' : 'initial',
        );
        playerStore.setActorAssetError(STAGE_PREVIEW_ACTOR_ID, null);
      })
      .catch((error) => {
        if (!isMounted) return;
        const resolvedError =
          error instanceof Error
            ? error
            : new Error('Failed to load BVH animation data.');
        setResolvedBvh(null);
        setIsLoading(false);
        setLoadError(resolvedError);
        playerStore.setActorAnimationStatus(STAGE_PREVIEW_ACTOR_ID, 'initial');
        playerStore.setActorAssetError(
          STAGE_PREVIEW_ACTOR_ID,
          resolvedError,
        );
      });

    return () => {
      isMounted = false;
    };
  }, [bvhUrl, playerStore]);

  useEffect(() => {
    pushCartographerDebugEvent('motionStage.animation.resolve.state', {
      hasMotionAsset: Boolean(resolvedBvh),
      status: isLoading ? 'pending' : resolvedBvh ? 'ready' : 'initial',
      isLoading,
      isReady: Boolean(resolvedBvh),
      error: loadError ? stringifyDebugReason(loadError) : null,
    });
  }, [isLoading, loadError, resolvedBvh]);

  if (loadError) {
    throw loadError;
  }

  if (!resolvedBvh) {
    return null;
  }

  return (
    <HeadlessResolvedBvhScenePlayer
      bvh={resolvedBvh}
      fileVersion={fileVersion}
      onPlaybackActiveChange={onPlaybackActiveChange}
    />
  );
}

function HeadlessResolvedGltfScenePlayer({
  motionAsset,
  fileVersion,
  onPlaybackActiveChange,
}: {
  motionAsset: CartographerMotionPreviewGltfAsset;
  fileVersion: number;
  onPlaybackActiveChange?: (active: boolean) => void;
}) {
  const cartographerStore = useContext(CartographerStoreContext);
  const playerStore = useContext(PlayerStoreContext);
  const {
    settings: {
      playback: {
        autoplay,
        disabled,
        frameRate,
        frameStepSize,
        loop,
        playbackSpeed,
      },
    },
  } = usePlayerSettings();
  const poseLocalsRef = useRef<THREE.Matrix4[]>([]);
  const suppressPauseRef = useRef(false);
  const firstPoseApplyLoggedRef = useRef(false);
  const firstFrameUpdateLoggedRef = useRef(false);
  const initializedMixerIdRef = useRef<string | null>(null);
  const mixerDurationFramesRef = useRef(0);
  const playbackActiveRef = useRef(false);
  const [playbackActive, setPlaybackActive] = useState(false);

  if (!cartographerStore) {
    throw new Error(
      'CartographerSceneMotionPreview must be rendered inside CartographerStoreProvider.',
    );
  }

  if (!playerStore) {
    throw new Error(
      'CartographerSceneMotionPreview must be rendered inside PlayerStoreProvider.',
    );
  }

  const poseBinding = useMemo(() => {
    const skeleton = cartographerStore.getSkeleton();
    if (!skeleton) return null;
    const binding = createThreeSkeletonPoseBinding(motionAsset.bones, skeleton, {
      // GLB motion files carry a real armature container whose transform
      // tells us the frame the bones were authored in. When the loaded
      // character's matching container differs (e.g. SKM_LongLegDaddy whose
      // `long_legs` group has a +90° X rotation but the motion's container
      // is identity), this option re-expresses the source root pose into the
      // target's parent frame so the resulting world orientation matches the
      // motion as authored. For characters whose containers already match
      // (e.g. SKM_Saitama with identity containers on both sides) this is a
      // no-op.
      useSourceParentAsAuthoredFrame: true,
    });
    const animatedSourceBoneNames = collectAnimatedSourceBoneNames(
      motionAsset.clip,
      motionAsset.bones,
    );
    if (animatedSourceBoneNames.size === 0) return binding;

    binding.bindings = binding.bindings.filter(
      ({ sourceBone }) =>
        sourceBoneHasBoneParent(sourceBone) ||
        animatedSourceBoneNames.has(sourceBone.name),
    );
    return binding;
  }, [cartographerStore, fileVersion, motionAsset]);

  useEffect(() => {
    const skeleton = cartographerStore.getSkeleton();
    pushCartographerDebugEvent('motionStage.player.mount', {
      fileVersion,
      sourceBoneCount: motionAsset.bones.length,
      targetBoneCount: skeleton?.bones.length ?? 0,
      poseBindingCount: poseBinding?.bindings.length ?? 0,
      clipDurationSeconds: motionAsset.clip.duration,
      clipTrackCount: motionAsset.clip.tracks.length,
      clipName: motionAsset.clip.name,
    });
    return () => {
      pushCartographerDebugEvent('motionStage.player.unmount', {
        fileVersion,
        sourceBoneCount: motionAsset.bones.length,
      });
    };
  }, [cartographerStore, fileVersion, motionAsset, poseBinding]);

  useEffect(() => {
    const skeleton = cartographerStore.getSkeleton();
    pushCartographerDebugEvent('motionStage.binding.state', {
      hasTargetSkeleton: Boolean(skeleton),
      targetBoneCount: skeleton?.bones.length ?? 0,
      sourceBoneCount: motionAsset.bones.length,
      poseBindingCount: poseBinding?.bindings.length ?? 0,
    });
  }, [cartographerStore, motionAsset.bones.length, poseBinding]);

  const applyCurrentPose = useCallback(
    (source: 'initialFrame' | 'frameUpdate') => {
      const skeleton = cartographerStore.getSkeleton();
      if (!skeleton || !poseBinding || poseBinding.bindings.length === 0) return;

      try {
        suppressPauseRef.current = true;
        sampleThreeSkeletonPoseLocals(poseBinding, skeleton, poseLocalsRef.current);
        cartographerStore.applyPreviewPoseLocals(poseLocalsRef.current, {
          notifyPoseVersion: !playbackActiveRef.current,
        });

        if (!firstPoseApplyLoggedRef.current) {
          firstPoseApplyLoggedRef.current = true;
          pushCartographerDebugEvent('motionStage.pose.apply.first', {
            source,
            targetBoneCount: skeleton.bones.length,
            poseBindingCount: poseBinding.bindings.length,
          });
        }
      } catch (error) {
        pushCartographerDebugEvent('motionStage.pose.apply.error', {
          source,
          error: stringifyDebugReason(error),
          errorStack: error instanceof Error ? error.stack ?? null : null,
          targetBoneCount: skeleton.bones.length,
          poseBindingCount: poseBinding.bindings.length,
        });
        throw error;
      }
    },
    [cartographerStore, poseBinding],
  );
  const handleFrameUpdate = useCallback(
    (frame: number) => {
      playerStore.setCurrentFrame(frame);
      if (!firstFrameUpdateLoggedRef.current) {
        firstFrameUpdateLoggedRef.current = true;
        pushCartographerDebugEvent('motionStage.frame.first', {
          frame,
          durationFrames: mixerDurationFramesRef.current,
        });
      }
      applyCurrentPose('frameUpdate');
    },
    [applyCurrentPose, playerStore],
  );
  const handlePlayChange = useCallback(
    (isPlaying: boolean) => {
      playbackActiveRef.current = isPlaying;
      playerStore.setIsPlaying(isPlaying);
      onPlaybackActiveChange?.(isPlaying);
      setPlaybackActive((current) => (current === isPlaying ? current : isPlaying));
      pushCartographerDebugEvent('motionStage.playback.state', {
        isPlaying,
      });
    },
    [onPlaybackActiveChange, playerStore],
  );
  const handleReady = useCallback(
    (duration: { seconds: number; frames: number }) => {
      mixerDurationFramesRef.current = duration.frames;
      playerStore.setDuration(duration);
      pushCartographerDebugEvent('motionStage.player.ready', {
        durationFrames: duration.frames,
        durationSeconds: duration.seconds,
      });
    },
    [playerStore],
  );

  const mixer = useFrameMixer(motionAsset.clip, motionAsset.scene, {
    autoplay,
    disabled: disabled || !poseBinding || poseBinding.bindings.length === 0,
    frameRate,
    frameStepSize,
    loop,
    onFrameUpdate: handleFrameUpdate,
    onPlayChange: handlePlayChange,
    onReady: handleReady,
    playbackSpeed,
  });
  const playbackLoopEnabled =
    playbackActive && !disabled && Boolean(poseBinding?.bindings.length);

  useEffect(() => {
    playerStore.setActorMixer(STAGE_PREVIEW_ACTOR_ID, mixer);
    pushCartographerDebugEvent('motionStage.actor.mixer.set', {
      actorId: STAGE_PREVIEW_ACTOR_ID,
    });
    return () => {
      playerStore.setActorMixer(STAGE_PREVIEW_ACTOR_ID, undefined);
      pushCartographerDebugEvent('motionStage.actor.mixer.clear', {
        actorId: STAGE_PREVIEW_ACTOR_ID,
      });
    };
  }, [mixer, playerStore]);

  useEffect(
    () => () => {
      playbackActiveRef.current = false;
      onPlaybackActiveChange?.(false);
    },
    [onPlaybackActiveChange],
  );

  useEffect(() => {
    mixerDurationFramesRef.current = mixer.duration.frames;
    playerStore.setDuration(mixer.duration);
  }, [mixer.duration, playerStore]);

  useEffect(() => {
    if (mixer.duration.frames <= 0) return;
    if (initializedMixerIdRef.current === mixer.id) return;

    initializedMixerIdRef.current = mixer.id;
    pushCartographerDebugEvent('motionStage.player.initialFrame', {
      durationFrames: mixer.duration.frames,
    });
    mixer.setFrame(0);
    applyCurrentPose('initialFrame');
  }, [applyCurrentPose, mixer, mixer.duration.frames]);

  useEffect(() => {
    if (!playbackLoopEnabled) return;

    let animationFrameId = 0;
    let previousTime = performance.now();

    const tick = (time: number) => {
      const delta = Math.max(0, (time - previousTime) / 1000);
      previousTime = time;
      mixer.update(delta);

      if (mixer.getPlayStatus()) {
        animationFrameId = window.requestAnimationFrame(tick);
        return;
      }

      setPlaybackActive(false);
    };

    animationFrameId = window.requestAnimationFrame((time) => {
      previousTime = time;
      animationFrameId = window.requestAnimationFrame(tick);
    });

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [mixer, playbackLoopEnabled]);

  useEffect(() => {
    return subscribe(cartographerStore.state, (operations) => {
      const poseVersionChanged = operations.some(
        ([, path]) => path[0] === 'poseVersion',
      );
      if (!poseVersionChanged) return;

      if (suppressPauseRef.current) {
        suppressPauseRef.current = false;
        return;
      }

      pushCartographerDebugEvent('motionStage.playback.paused.externalPoseChange');
      playerStore.pause(STAGE_PREVIEW_ACTOR_ID);
    });
  }, [cartographerStore, playerStore]);

  return null;
}

function HeadlessGltfScenePlayer({
  gltfUrl,
  fileVersion,
  onPlaybackActiveChange,
}: {
  gltfUrl: string;
  fileVersion: number;
  onPlaybackActiveChange?: (active: boolean) => void;
}) {
  const playerStore = useContext(PlayerStoreContext);
  const { asset: motionAsset, error: loadError, isLoading } =
    useMotionPreviewGltfAsset({
      enabled: Boolean(gltfUrl),
      gltfUrl,
    });

  if (!playerStore) {
    throw new Error(
      'CartographerSceneMotionPreview must be rendered inside PlayerStoreProvider.',
    );
  }

  useEffect(() => {
    playerStore.registerActor(STAGE_PREVIEW_ACTOR_ID);
    playerStore.setActiveActor(STAGE_PREVIEW_ACTOR_ID);
    pushCartographerDebugEvent('motionStage.actor.register', {
      actorId: STAGE_PREVIEW_ACTOR_ID,
      fileVersion,
    });
    return () => {
      playerStore.unregisterActor(STAGE_PREVIEW_ACTOR_ID);
      pushCartographerDebugEvent('motionStage.actor.unregister', {
        actorId: STAGE_PREVIEW_ACTOR_ID,
      });
    };
  }, [fileVersion, playerStore]);

  useEffect(() => {
    playerStore.setActorAnimationStatus(
      STAGE_PREVIEW_ACTOR_ID,
      isLoading ? 'pending' : motionAsset ? 'ready' : 'initial',
    );
    playerStore.setActorAssetError(STAGE_PREVIEW_ACTOR_ID, loadError);
  }, [isLoading, loadError, motionAsset, playerStore]);

  useEffect(() => {
    pushCartographerDebugEvent('motionStage.animation.resolve.state', {
      hasMotionAsset: Boolean(motionAsset),
      status: isLoading ? 'pending' : motionAsset ? 'ready' : 'initial',
      isLoading,
      isReady: Boolean(motionAsset),
      error: loadError ? stringifyDebugReason(loadError) : null,
    });
  }, [isLoading, loadError, motionAsset]);

  if (loadError) {
    throw loadError;
  }

  if (!motionAsset) {
    return null;
  }

  return (
    <HeadlessResolvedGltfScenePlayer
      motionAsset={motionAsset}
      fileVersion={fileVersion}
      onPlaybackActiveChange={onPlaybackActiveChange}
    />
  );
}

function CartographerSceneMotionPreview({
  apiKey,
  characterID,
  motionID,
  motionLabel = null,
  bodyIndex,
  fileVersion,
  mappingRevisionKey,
  assetKind,
  configSource = 'saved',
  prepareForUserMotionRequest,
  onSwitchToUnsavedRetarget,
  onRetargetStatusChange,
  onPlaybackActiveChange,
  onOpenFullPreview,
  onClose,
}: CartographerSceneMotionPreviewProps) {
  const cartographerStore = useContext(CartographerStoreContext);
  const poseSnapshotRef = useRef<CartographerPoseSnapshot | null>(null);
  const effectiveMotionLabel = motionLabel?.trim() || motionID || 'Motion preview';
  const refreshKey = `${characterID}|${motionID ?? ''}|${bodyIndex ?? 0}|${fileVersion}`;

  if (!cartographerStore) {
    throw new Error(
      'CartographerSceneMotionPreview must be rendered inside CartographerStoreProvider.',
    );
  }

  const motionDownloadType = resolveMotionPreviewDownloadType(assetKind);
  const { loading, mainError, previewOutOfDate, refreshNow, motionUrl } =
    useCartographerMotionRetargetPreview({
      enabled: Boolean(apiKey && characterID && motionID),
      apiKey,
      motionID,
      characterID,
      bodyIndex,
      configSource,
      downloadType: motionDownloadType,
      fps: STAGE_PREVIEW_FRAME_RATE,
      frameStep: STAGE_PREVIEW_FRAME_STEP,
      rootMotion: true,
      refreshKey,
      stateRevisionKey: mappingRevisionKey,
      resetOnDisable: false,
      prepareForUserMotionRequest,
    });

  useEffect(() => {
    onRetargetStatusChange?.({
      source: 'stage',
      motionID,
      bodyIndex,
      configSource,
      mappingRevisionKey,
      exportSettingsKey: getMotionExportSettingsKey(
        DEFAULT_MOTION_EXPORT_SETTINGS,
      ),
      userMotionUrl: motionUrl,
      loading,
      mainError,
      previewOutOfDate,
    });
  }, [
    bodyIndex,
    configSource,
    loading,
    mainError,
    mappingRevisionKey,
    motionID,
    onRetargetStatusChange,
    previewOutOfDate,
    motionUrl,
  ]);

  useEffect(() => {
    pushCartographerDebugEvent('motionStage.component.mount', {
      characterID,
      motionID,
      configSource,
      fileVersion,
      mappingRevisionKey,
    });
    return () => {
      pushCartographerDebugEvent('motionStage.component.unmount', {
        characterID,
        motionID,
      });
    };
  }, [characterID, configSource, fileVersion, mappingRevisionKey, motionID]);

  useEffect(() => {
    pushCartographerDebugEvent('motionStage.retarget.state', {
      motionID,
      assetKind,
      motionDownloadType,
      loading,
      hasMotionUrl: Boolean(motionUrl),
      previewOutOfDate,
      mainError,
    });
  }, [
    assetKind,
    loading,
    mainError,
    motionDownloadType,
    motionID,
    previewOutOfDate,
    motionUrl,
  ]);

  useEffect(() => {
    poseSnapshotRef.current = cartographerStore.capturePoseSnapshot();
    pushCartographerDebugEvent('motionStage.pose.snapshot.captured', {
      hasSnapshot: Boolean(poseSnapshotRef.current),
      poseMatrixCount: poseSnapshotRef.current?.length ?? 0,
    });
    return () => {
      pushCartographerDebugEvent('motionStage.pose.snapshot.restoring', {
        hasSnapshot: Boolean(poseSnapshotRef.current),
        poseMatrixCount: poseSnapshotRef.current?.length ?? 0,
      });
      cartographerStore.restorePoseSnapshot(poseSnapshotRef.current);
      poseSnapshotRef.current = null;
    };
  }, [cartographerStore, configSource, fileVersion, motionID]);

  const handleRefreshPreview = useCallback(async () => {
    pushCartographerDebugEvent('motionStage.refresh.requested', {
      motionID,
      configSource,
    });
    if (configSource === 'temp') {
      refreshNow();
      return;
    }
    if (!onSwitchToUnsavedRetarget) return;
    const didSwitch = await onSwitchToUnsavedRetarget();
    pushCartographerDebugEvent('motionStage.refresh.unsavedSwitch.result', {
      motionID,
      didSwitch,
    });
    if (!didSwitch) return;
    refreshNow({ configSource: 'temp' });
  }, [configSource, motionID, onSwitchToUnsavedRetarget, refreshNow]);

  if (!motionID) {
    return null;
  }

  return (
    <Player.Root
      key={`${refreshKey}|${assetKind}|${motionUrl ?? 'pending'}`}
      id={STAGE_PREVIEW_PLAYER_ID}
      hotkeysEnabled={false}
      initialSettings={{
        character: { showSkeleton: false },
        playback: {
          autoplay: true,
          frameRate: STAGE_PREVIEW_FRAME_RATE,
          frameStepSize: STAGE_PREVIEW_FRAME_STEP,
          loop: true,
          playbackSpeed: 1,
        },
      }}
    >
      {motionUrl ? (
        assetKind === 'gltf' ? (
          <HeadlessGltfScenePlayer
            gltfUrl={motionUrl}
            fileVersion={fileVersion}
            onPlaybackActiveChange={onPlaybackActiveChange}
          />
        ) : (
          <HeadlessBvhScenePlayer
            bvhUrl={motionUrl}
            fileVersion={fileVersion}
            onPlaybackActiveChange={onPlaybackActiveChange}
          />
        )
      ) : null}
      <SceneMotionFooter
        configSource={configSource}
        loading={loading}
        mainError={mainError}
        motionLabel={effectiveMotionLabel}
        previewOutOfDate={previewOutOfDate}
        motionUrl={motionUrl}
        onOpenFullPreview={onOpenFullPreview}
        onClose={onClose}
        onRefreshPreview={handleRefreshPreview}
      />
    </Player.Root>
  );
}

export default memo(CartographerSceneMotionPreview);
